import { API_BASE_URL } from "../config";
import fetchWithAuth from "../middlewares/fetchWithAuth";

const LIVE_TAG_URL = `${API_BASE_URL}/liveTag`;

async function parseResponse(response) {
  if (!response) return null;

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    return null;
  }

  return response.json();
}

async function handleResponse(response, fallbackMessage, options = {}) {
  const { allowMissingArray = false, allowMissingNull = false } = options;

  if (!response) {
    return allowMissingArray ? [] : null;
  }

  if (response.ok) {
    const data = await parseResponse(response);
    if (data == null && allowMissingArray) return [];
    return data;
  }

  if ((response.status === 404 || response.status === 501) && allowMissingArray) {
    return [];
  }

  if ((response.status === 404 || response.status === 501) && allowMissingNull) {
    return null;
  }

  let message = fallbackMessage;

  try {
    const payload = await parseResponse(response);
    message = payload?.message || payload?.error || message;
  } catch {
    // Mantener mensaje por defecto.
  }

  const error = new Error(message);
  error.status = response.status;
  throw error;
}

export async function getUserSessions(navigate) {
  const response = await fetchWithAuth(
    `${LIVE_TAG_URL}/sessions`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
    navigate
  );

  return handleResponse(response, "Error al obtener las sesiones de Live Tag", {
    allowMissingArray: true,
  });
}

export async function getSessionEvents(sessionId, navigate) {
  if (!sessionId) return [];

  const response = await fetchWithAuth(
    `${LIVE_TAG_URL}/sessions/${encodeURIComponent(String(sessionId))}/events`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
    navigate
  );

  return handleResponse(response, "Error al obtener los eventos de la sesión", {
    allowMissingArray: true,
  });
}

export async function createSession(payload, navigate) {
  const response = await fetchWithAuth(
    `${LIVE_TAG_URL}/sessions`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
    navigate
  );

  return handleResponse(response, "Error al crear la sesión de Live Tag", {
    allowMissingNull: true,
  });
}

export async function addEvent(sessionId, payload, navigate) {
  if (!sessionId) return null;

  const response = await fetchWithAuth(
    `${LIVE_TAG_URL}/sessions/${encodeURIComponent(String(sessionId))}/events`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
    navigate
  );

  return handleResponse(response, "Error al registrar el evento en Live Tag", {
    allowMissingNull: true,
  });
}

export async function updateEvent(sessionId, eventId, payload, navigate) {
  if (!sessionId || !eventId) return null;

  const response = await fetchWithAuth(
    `${LIVE_TAG_URL}/sessions/${encodeURIComponent(String(sessionId))}/events/${encodeURIComponent(String(eventId))}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
    navigate
  );

  return handleResponse(response, "Error al actualizar el evento de Live Tag");
}

export async function updateSession(sessionId, payload, navigate) {
  if (!sessionId) return null;

  const response = await fetchWithAuth(
    `${LIVE_TAG_URL}/sessions/${encodeURIComponent(String(sessionId))}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
    navigate
  );

  const saved = await handleResponse(response, "Error al actualizar la sesión de Live Tag");
  if (!saved) throw new Error("No se pudo guardar la sesión de Live Tag");
  if (payload?.clockState && (
    !Number.isSafeInteger(saved.clockRevision)
    || saved.clockState?.updateId !== payload.clockState.updateId
  )) throw new Error("El servidor no ha confirmado el cronómetro");
  return saved;
}

export async function getSession(sessionId, navigate) {
  const response = await fetchWithAuth(
    `${LIVE_TAG_URL}/sessions/${encodeURIComponent(String(sessionId))}`,
    { method: "GET", headers: { "Content-Type": "application/json" } },
    navigate
  );
  const session = await handleResponse(response, "Error al obtener la sesión de Live Tag");
  if (!session) throw new Error("No se pudo cargar la sesión de Live Tag");
  return session;
}

export async function deleteSession(sessionId, navigate) {
  if (!sessionId) return null;

  const response = await fetchWithAuth(
    `${LIVE_TAG_URL}/sessions/${encodeURIComponent(String(sessionId))}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    },
    navigate
  );

  return handleResponse(response, "Error al eliminar la sesión de Live Tag", {
    allowMissingNull: true,
  });
}

const liveTagService = {
  getSession,
  getUserSessions,
  getSessionEvents,
  createSession,
  addEvent,
  updateSession,
  deleteSession,
};

export default liveTagService;
